import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./Collections.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditCollection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [collectors, setCollectors] = useState([]);
  
  const [formData, setFormData] = useState({
    customerId: "",
    houseNo: "",
    collectorId: "",
    collectionStatus: "Yes", // Changed from 'status' to 'collectionStatus'
    description: ""
  });

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionRes, customersRes, collectorsRes] = await Promise.all([
          axios.get(`${url}/api/collection/${id}`),
          axios.get(`${url}/api/customer`),
          axios.get(`${url}/api/collectors`)
        ]);

        const collection = collectionRes.data.data;
        
        setFormData({
          customerId: collection.customer?._id || "",
          houseNo: collection.houseNo || "",
          collectorId: collection.collector?._id || "",
          collectionStatus: collection.collectionStatus || "Yes", // Changed from 'status' to 'collectionStatus'
          description: collection.description || ""
        });

        setCustomers(customersRes.data.data || []);
        setCollectors(collectorsRes.data.data || []);
      } catch (error) {
        toast.error("Failed to load collection data");
        navigate("/collections");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-fill house number and collector when customer changes
    if (name === "customerId") {
      const selectedCustomer = customers.find(c => c._id === value);
      if (selectedCustomer) {
        setFormData(prev => ({ 
          ...prev, 
          customerId: value,
          houseNo: selectedCustomer.houseNo.toString(),
          collectorId: selectedCustomer.collector?._id || ""
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customerId || !formData.houseNo || !formData.collectorId) {
      toast.error("All fields are required");
      return;
    }

    setSubmitting(true);

    try {
      // Create payload with correct field name 'collectionStatus'
      const payload = {
        customerId: formData.customerId,
        houseNo: formData.houseNo,
        collectorId: formData.collectorId,
        collectionStatus: formData.collectionStatus, // Using collectionStatus
        description: formData.description
      };

      console.log("📤 Submitting update:", payload);

      const response = await axios.put(`${url}/api/collection/${id}`, payload);
      
      if (response.data.success) {
        toast.success("Collection updated successfully!");
        navigate("/collections");
      }
    } catch (error) {
      console.error("❌ Update error:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to update collection");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading collection...</div>;

  return (
    <div className="district-container">
      <div className="header">
        <h2>Edit Collection</h2>
        <button onClick={() => navigate("/collections")} className="add-button">
          Back to List
        </button>
      </div>

      <form className="district-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Customer</label>
          <select
            name="customerId"
            value={formData.customerId}
            onChange={handleChange}
            required
          >
            <option value="">Select Customer</option>
            {customers.map(c => (
              <option key={c._id} value={c._id}>
                {c.customerName} (House: {c.houseNo})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>House Number</label>
          <input
            type="text"
            name="houseNo"
            value={formData.houseNo}
            onChange={handleChange}
            placeholder="House number"
            required
            readOnly
          />
        </div>

        <div className="form-group">
          <label>Collector</label>
          <input
            type="text"
            value={collectors.find(c => c._id === formData.collectorId)?.fname || ''}
            placeholder="Auto-filled from customer"
            readOnly
            disabled
          />
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            name="collectionStatus" // Changed from 'status' to 'collectionStatus'
            value={formData.collectionStatus}
            onChange={handleChange}
            required
          >
            <option value="Yes">Yes (Collected)</option>
            <option value="No">No (Not Collected)</option>
          </select>
        </div>

        <div className="form-group">
          <label>Description (Optional)</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter any notes"
            rows="3"
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? "Updating..." : "Update Collection"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/collections")}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCollection;